import React, { useState, useEffect, useContext, useCallback } from 'react'
import Select from 'react-select'
import { Option, FormField } from 'shared/types'
import { Props as SelectProps } from 'react-select/src/Select'
import cn from 'classnames'
// @ts-ignore
import TextFieldMessage from 'react-md/lib/TextFields/TextFieldMessage'
import styled, { ThemeContext } from 'styled-components'
import Box from '../Box'
import { FieldLabel } from '../TextField'

type SelectAutocompleteProps = Omit<SelectProps, 'options'> &
  FormField & { onScrollBottom?: (e: any) => void; marginBottom?: string | number }

function SelectAutocomplete(props: SelectAutocompleteProps) {
  const {
    label,
    error,
    required,
    className = '',
    id,
    value,
    options = [],
    onScrollBottom,
    onChange,
    disabled,
    marginBottom,
    ...restProps
  } = props
  const [currentValue, setCurrentValue] = useState<Option | Option[] | null>(setInitialValue)

  const isNotEqualValue = useCallback(() => {
    if (!value && !currentValue) return false
    if (props.isMulti && Array.isArray(currentValue)) {
      return value.join(',') !== currentValue.map(e => (typeof e === 'object' ? e.value : e)).join(',')
    }
    return value !== (currentValue as Option)?.value
  }, [value, currentValue, props.isMulti])

  useEffect(() => {
    if (isNotEqualValue()) {
      if (props.isMulti) {
        const filtered = (value?.map((e: string | number) => options.find(ee => ee.value === e)) || []).filter(
          (each: any) => each !== undefined,
        )

        if (filtered.length || !value?.length) setCurrentValue(filtered)
      } else {
        const selected = options.find(e => e.value === value)
        if (selected !== undefined) setCurrentValue(selected)
      }
    }
  }, [value, options, props.isMulti])

  const theme = useContext(ThemeContext)

  const customStyles = {
    menuPortal: (styles: Object) => ({ ...styles, zIndex: 9999 }),
    control: (styles: Object) => ({
      ...styles,
      backgroundColor: theme.colors.white,
      fontSize: '16px',
      color: theme.colors.secondary,
      minHeight: '50px',
      borderRadius: theme.borderRadius,
      border: `1px solid ${theme.colors.inputBorder}`,
      outline: '0',
      boxShadow: 'none',
      ':hover': {
        border: `1px solid ${theme.colors.main}`,
        boxShadow: `0px 0px 5px 1px ${theme.colors.focusShadow}`,
      },
      active: {
        border: `1px solid ${theme.colors.main}`,
        boxShadow: `0px 0px 5px 1px ${theme.colors.focusShadow}`,
      },
      focus: {
        border: `1px solid ${theme.colors.main}`,
        boxShadow: `0px 0px 5px 1px ${theme.colors.focusShadow}`,
      },
    }),
    option: (styles: Object, { isFocused }: any) => ({
      ...styles,
      color: theme.colors.secondary,
      backgroundColor: isFocused ? theme.colors.tableAlternate : theme.colors.white,
      minHeight: '32px',
      fontSize: '16px',
      fontWeight: 500,
    }),
    singleValue: (styles: Object) => ({
      ...styles,
      color: theme.colors.secondary,
    }),
    multiValue: (styles: Object) => {
      return {
        ...styles,
        backgroundColor: theme.colors.tableAlternate,
        borderRadius: theme.borderRadius,
      }
    },
    dropdownIndicator: (styles: Object) => ({
      ...styles,
      color: '#727272',
      ':hover': {
        color: '#727272',
      },
      active: {
        color: '#727272',
      },
      focus: {
        color: '#727272',
      },
    }),
  }

  return (
    <SelectAutoCompleteContainer className={className} marginBottom={marginBottom}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <div>
        <Select
          id={id}
          value={currentValue}
          options={options}
          onChange={handleChange}
          isDisabled={disabled}
          className="select-field"
          classNamePrefix="select-field"
          onMenuScrollToBottom={onScrollBottom}
          styles={customStyles}
          {...restProps}
        />
        <TextFieldMessage errorText={error} error={!!error} />
      </div>
    </SelectAutoCompleteContainer>
  )

  function setInitialValue(): Option | Option[] {
    if (props.isMulti && Array.isArray(value)) {
      const filtered = value.map(i => options.find(j => j.value === i)).filter((each: any) => each !== undefined)

      return filtered as Option[]
    }
    return options.find(i => i.value === value) as Option
  }

  function handleChange(newVal: any) {
    if (props.isMulti) {
      onChange(Array.isArray(newVal) ? newVal.map(e => e.value) : [], id)
      setCurrentValue(Array.isArray(newVal) ? [...newVal] : [])
    } else {
      onChange(newVal && 'value' in newVal ? newVal.value || '' : '', id)
      setCurrentValue(newVal)
    }
  }
}

const SelectAutoCompleteContainer = styled(Box)`
  width: 100%;

  .select-field {
    &__indicator-separator {
      display: none;
    }

    &__menu {
      z-index: 2;
    }

    &__menu-list {
      &::-webkit-scrollbar {
        width: 8px;
        background-color: #f5f5f5;
        border-radius: 5px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: ${({ theme }) => theme.colors.label};
        border-radius: 50px;
      }
    }
  }

  .select-field__control--is-disabled {
    background: #dee1e1;
  }
`

SelectAutocomplete.defaultProps = {
  disabled: false,
  required: true,
  className: '',
  error: '',
  options: [],
}

export default SelectAutocomplete
